// src/components/capabilities-v3/CapabilitiesV3.js
// PROPER STICKY - Pill + Text + Stacking Cards

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, useScroll, useTransform, useSpring, useMotionTemplate, AnimatePresence } from 'framer-motion';
import { Sparkles, Brain, BarChart3, Code2, Calendar, FileText, Wrench, Zap, Activity, Search, Settings, AlertCircle, Layout, Cloud, Shield, Layers, X } from 'lucide-react';
import { pillarsData } from '../../data/capabilitiesData';
import './CapabilitiesV3.css';

// Icon mappings
const iconMap = { Brain, BarChart3, Code2 };
const bulletIconMap = { Calendar, FileText, Wrench, Zap, Activity, Search, Settings, AlertCircle, Layout, Cloud, Shield, Layers };

// Stacking Card Component
const StackingCard = ({ pillar, index, scrollYProgress }) => {
  const IconComponent = iconMap[pillar.icon];
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleImageClick = () => setIsModalOpen(true);
  const handleModalClose = () => setIsModalOpen(false);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsModalOpen(false);
    };
    if (isModalOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  // Card timing - cards appear after text settles (40%)
  // Spread cards across 45% to 75% of scroll
  const cardRanges = [
    { start: 0.45, peak: 0.53 },   // Card 0: 45-53%
    { start: 0.55, peak: 0.63 },   // Card 1: 55-63%
    { start: 0.65, peak: 0.73 },   // Card 2: 65-73%
  ];
  const { start: cardStart, peak: cardPeak } = cardRanges[index] || cardRanges[0];

  // Stack offset - cards stack with slight vertical offset
  const stackOffset = index * 8;

  // Card entrance: slides up from below
  const y = useTransform(scrollYProgress, [cardStart, cardPeak], [200, stackOffset]);

  // Card opacity: fades in
  const opacity = useTransform(scrollYProgress, [cardStart, cardStart + 0.04], [0, 1]);

  // Card scale: starts slightly smaller
  const scale = useTransform(scrollYProgress, [cardStart, cardPeak], [0.92, 1]);

  // Previous cards: scale down when next card appears
  const nextCardRange = cardRanges[index + 1];
  const nextCardStart = nextCardRange ? nextCardRange.start : 0.90;
  const scaleDown = useTransform(scrollYProgress, [nextCardStart, nextCardStart + 0.08], [1, 0.95]);

  // Combine scales
  const combinedScale = useTransform([scale, scaleDown], ([s, sd]) => s * sd);

  // Darken overlay for previous cards
  const overlayOpacity = useTransform(scrollYProgress, [nextCardStart, nextCardStart + 0.08], [0, 0.3]);

  // Smooth springs
  const smoothY = useSpring(y, { stiffness: 80, damping: 25 });
  const smoothOpacity = useSpring(opacity, { stiffness: 100, damping: 30 });
  const smoothScale = useSpring(combinedScale, { stiffness: 100, damping: 30 });
  const smoothOverlay = useSpring(overlayOpacity, { stiffness: 100, damping: 30 });

  // Convert hex accent color to RGB for rgba() usage
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '0, 217, 255';
  };

  return (
    <>
      <motion.div
        className="stack-card"
        style={{
          y: smoothY,
          opacity: smoothOpacity,
          scale: smoothScale,
          zIndex: index + 1,
          '--card-accent': pillar.accent,
          '--card-accent-rgb': hexToRgb(pillar.accent),
        }}
      >
        <motion.div className="stack-card__overlay" style={{ opacity: smoothOverlay }} />

        <div className="stack-card__inner">
          <div className="stack-card__header">
            <div className="stack-card__label">
              <IconComponent size={16} />
              <span>{pillar.label}</span>
            </div>
            <h3 className="stack-card__title">{pillar.title}</h3>
          </div>

          <div className="stack-card__content">
            <div className="stack-card__bullets">
              <ul>
                {pillar.bullets.map((bullet, i) => {
                  const BulletIcon = bulletIconMap[pillar.bulletIcons?.[i]] || Sparkles;
                  return (
                    <li key={i}>
                      <BulletIcon size={14} className="bullet-icon" />
                      <span>{bullet}</span>
                    </li>
                  );
                })}
              </ul>
              <div className="stack-card__tags">
                {pillar.tags.map((tag, i) => (
                  <span key={i} className="stack-card__tag">{tag}</span>
                ))}
              </div>
            </div>

            <div className="stack-card__image-zone">
              <div
                className="stack-card__image-frame"
                onClick={handleImageClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleImageClick()}
              >
                <img
                  src={pillar.image}
                  alt={pillar.title}
                  className="stack-card__image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const placeholder = e.target.parentElement.querySelector('.stack-card__placeholder');
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
                <div className="stack-card__placeholder">
                  <IconComponent size={40} />
                  <span>Image Coming Soon</span>
                </div>
                <div className="stack-card__image-hover">
                  <span>Click to expand</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Image Modal */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              className="stack-modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={handleModalClose}
            >
              {/* Multiple animated gradient blobs */}
              <div className="stack-modal__glow" />
              <div className="stack-modal__glow" />
              <div className="stack-modal__glow" />
              <button className="stack-modal__close" onClick={handleModalClose} aria-label="Close modal">
                <X size={24} />
              </button>
              <motion.div
                className="stack-modal__content"
                initial={{ scale: 0.85, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.85, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                <img src={pillar.image} alt={pillar.title} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

const CapabilitiesV3 = () => {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  // ============================================
  // SCROLL TIMELINE (500vh section)
  // ============================================

  // PILL: Fades in when section top reaches viewport center
  const pillOpacity = useTransform(scrollYProgress, [0.06, 0.10], [0, 1]);
  const smoothPillOpacity = useSpring(pillOpacity, { stiffness: 100, damping: 30 });

  // TEXT: Starts appearing after pill is at top (~14%)
  const line1Opacity = useTransform(scrollYProgress, [0.14, 0.18], [0, 1]);
  const line2Opacity = useTransform(scrollYProgress, [0.18, 0.22], [0, 1]);
  const line3Opacity = useTransform(scrollYProgress, [0.22, 0.26], [0, 1]);
  const subtitleOpacity = useTransform(scrollYProgress, [0.26, 0.30], [0, 1]);

  // TEXT MOVE LEFT: After all lines visible (30%), move from center to left edge
  const textXPercent = useTransform(scrollYProgress, [0.30, 0.40], [0, -34]);

  // Overall text container opacity
  const textOpacity = useTransform(scrollYProgress, [0.13, 0.15], [0, 1]);

  // CARDS CONTAINER: Fades in as text moves left (38-44%)
  const cardsOpacity = useTransform(scrollYProgress, [0.38, 0.44], [0, 1]);

  // Smooth springs
  const smoothTextOpacity = useSpring(textOpacity, { stiffness: 100, damping: 30 });
  const smoothLine1 = useSpring(line1Opacity, { stiffness: 100, damping: 30 });
  const smoothLine2 = useSpring(line2Opacity, { stiffness: 100, damping: 30 });
  const smoothLine3 = useSpring(line3Opacity, { stiffness: 100, damping: 30 });
  const smoothSubtitle = useSpring(subtitleOpacity, { stiffness: 100, damping: 30 });
  const smoothTextX = useSpring(textXPercent, { stiffness: 80, damping: 25 });
  const smoothCardsOpacity = useSpring(cardsOpacity, { stiffness: 100, damping: 30 });

  // Convert numeric value to vw units for CSS
  const textXWithUnits = useMotionTemplate`${smoothTextX}vw`;

  return (
    <section ref={sectionRef} className="cap-v3-section">
      {/* ============================================
          SINGLE TRACK - Contains all sticky elements
          ============================================ */}
      <div className="cap-v3-track">

        {/* PILL - Sticky at top: 24px */}
        <motion.div
          className="cap-v3-pill"
          style={{ opacity: smoothPillOpacity }}
        >
          <Sparkles size={14} />
          <span>Our Capabilities</span>
        </motion.div>

        {/* TEXT - Sticky at top: 40vh */}
        <motion.div
          className="cap-v3-text"
          style={{
            opacity: smoothTextOpacity,
            x: textXWithUnits,
          }}
        >
          <h2 className="cap-v3-title">
            <motion.span style={{ opacity: smoothLine1 }}>
              Three Pillars of
            </motion.span>
            <motion.span className="gradient-text" style={{ opacity: smoothLine2 }}>
              Operational
            </motion.span>
            <motion.span className="gradient-text" style={{ opacity: smoothLine3 }}>
              Excellence
            </motion.span>
          </h2>
          <motion.p className="cap-v3-subtitle" style={{ opacity: smoothSubtitle }}>
            We combine cutting-edge AI technology, advanced analytics, and custom software development to transform how enterprises operate.
          </motion.p>
        </motion.div>

        {/* CARDS - Sticky on right side */}
        <motion.div
          className="cap-v3-cards"
          style={{ opacity: smoothCardsOpacity }}
        >
          <div className="cap-v3-cards__stack">
            {pillarsData.map((pillar, index) => (
              <StackingCard
                key={pillar.id}
                pillar={pillar}
                index={index}
                scrollYProgress={scrollYProgress}
              />
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default CapabilitiesV3;
